<script setup lang="ts" generic="T">
import { onBeforeUnmount, onMounted, ref } from 'vue';

const props = withDefaults(defineProps<{
  items: T[];
  /** Items matching this predicate take every column instead of one. */
  isFullWidth?: (item: T) => boolean;
  /** Spacing between cards, in px. Also reserved below the last row. */
  gap?: number;
  /** Tailwind `grid-template-columns` classes; spans adapt to any count. */
  columnsClass?: string;
}>(), {
  gap: 12,
  columnsClass: 'grid-cols-1 md:grid-cols-2',
});

/**
 * Rows are 1px tall, so each card can span exactly as many rows as it is tall
 * and `dense` flow pulls later cards up into the leftover space. Heights come
 * from a ResizeObserver, so cards that grow (accordions, lazy data) re-pack.
 */
const spans = ref<number[]>([]);
const measured = ref(false);

const contentEls: (HTMLElement | null)[] = [];
const indexByEl = new WeakMap<Element, number>();

const measure = (el: HTMLElement, index: number) => {
  spans.value[index] = Math.ceil(el.getBoundingClientRect().height) + props.gap;
};

const observer = new ResizeObserver(entries => {
  for (const entry of entries) {
    const index = indexByEl.get(entry.target);
    if (index !== undefined) measure(entry.target as HTMLElement, index);
  }
  measured.value = true;
});

const setContentRef = (el: unknown, index: number) => {
  const previous = contentEls[index];
  if (previous && previous !== el) observer?.unobserve(previous);
  contentEls[index] = (el as HTMLElement | null) ?? null;
  if (el) {
    indexByEl.set(el as Element, index);
    observer?.observe(el as Element);
  }
};

// Measure synchronously on mount so the grid is packed before the first paint.
onMounted(() => {
  contentEls.forEach((el, index) => el && measure(el, index));
  measured.value = true;
});

onBeforeUnmount(() => observer?.disconnect());
</script>

<template>
  <div
    class="grid"
    :class="columnsClass"
    :style="{
      columnGap: `${gap}px`,
      rowGap: '0px',
      gridAutoFlow: 'row dense',
      gridAutoRows: measured ? '1px' : 'auto',
    }"
  >
    <div
      v-for="(item, index) in items"
      :key="index"
      :style="{
        gridColumn: isFullWidth?.(item) ? '1 / -1' : undefined,
        gridRowEnd: measured && spans[index] ? `span ${spans[index]}` : undefined,
        paddingBottom: `${gap}px`,
      }"
    >
      <div :ref="el => setContentRef(el, index)">
        <slot :item="item" :index="index" />
      </div>
    </div>
  </div>
</template>
